import { useMemo, useState } from 'react';
import { sendLead, type LeadPayload, type ValidationResult } from '../lib/maytoMail';
import { Icon } from '../lib/icons';
import { cn } from '../lib/cn';

const serviceOptions = [
  'Custom Software Development',
  'Automation Systems',
  'Infrastructure Services',
  'AI Systems',
  'Network & WiFi',
  'Managed Hosting',
  'Homelab Services',
  'Long-Term Support'
];

const budgetOptions = [
  'Under $2,500',
  '$2,500 - $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000+',
  'Monthly support plan',
  'Not sure yet'
];

const timelineOptions = ['As soon as practical', 'Next 30 days', '1-3 months', '3-6 months', 'Planning ahead'];

type LeadFormProps = {
  formType: LeadPayload['formType'];
  title?: string;
  compact?: boolean;
};

const emptyForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  serviceType: '',
  budgetRange: '',
  timeline: '',
  description: '',
  website: ''
};

export function LeadForm({ formType, title, compact = false }: LeadFormProps) {
  const [values, setValues] = useState(emptyForm);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formId = useMemo(() => `${formType}-${Math.random().toString(36).slice(2)}`, [formType]);

  const update = (field: keyof typeof values, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');
    setErrors({});

    try {
      await sendLead({ formType, ...values, startedAt });
      setStatus('success');
      setMessage('Thanks. Your request was sent. MaytoMation will follow up with clarifying questions and next steps.');
      setValues(emptyForm);
      setStartedAt(Date.now());
    } catch (error) {
      const validation = (error as Error & { validation?: ValidationResult }).validation;
      setErrors(validation?.errors ?? {});
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className={cn(
        'rounded-lg border border-brand-border bg-white p-5 shadow-card sm:p-7',
        compact ? 'space-y-4' : 'space-y-5'
      )}
      noValidate
    >
      {title ? <h2 className="font-display text-3xl text-brand-dark">{title}</h2> : null}
      {errors.form ? <p className="rounded-md bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errors.form}</p> : null}
      <input
        type="text"
        name="website"
        value={values.website}
        onChange={(event) => update('website', event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" error={errors.name}>
          <input
            value={values.name}
            onChange={(event) => update('name', event.target.value)}
            autoComplete="name"
            className="form-field"
            required
          />
        </Field>
        <Field label="Company">
          <input
            value={values.company}
            onChange={(event) => update('company', event.target.value)}
            autoComplete="organization"
            className="form-field"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email" error={errors.email}>
          <input
            type="email"
            value={values.email}
            onChange={(event) => update('email', event.target.value)}
            autoComplete="email"
            className="form-field"
            required
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={values.phone}
            onChange={(event) => update('phone', event.target.value)}
            autoComplete="tel"
            className="form-field"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Service Type">
          <select
            value={values.serviceType}
            onChange={(event) => update('serviceType', event.target.value)}
            className="form-field"
          >
            <option value="">Select service</option>
            {serviceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Budget Range">
          <select
            value={values.budgetRange}
            onChange={(event) => update('budgetRange', event.target.value)}
            className="form-field"
          >
            <option value="">Select budget</option>
            {budgetOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Timeline">
          <select
            value={values.timeline}
            onChange={(event) => update('timeline', event.target.value)}
            className="form-field"
          >
            <option value="">Select timeline</option>
            {timelineOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Description" error={errors.description}>
        <textarea
          value={values.description}
          onChange={(event) => update('description', event.target.value)}
          className="form-field min-h-36 resize-y"
          placeholder="Tell us what is slowing the team down, what systems are involved, and what a good outcome would look like."
          required
        />
      </Field>

      {message ? (
        <p
          className={cn(
            'rounded-md px-4 py-3 text-sm font-semibold',
            status === 'success' ? 'bg-teal-50 text-brand-navy' : 'bg-red-50 text-red-700'
          )}
          role="status"
        >
          {message}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-md bg-brand-dark px-6 py-3 text-sm font-bold text-white shadow-card transition hover:bg-brand-navy disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === 'loading' ? 'Sending request...' : formType === 'booking' ? 'Request Consultation' : 'Send Project Details'}
        <Icon name="send" className="h-4 w-4" />
      </button>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-brand-dark">
      <span>{label}</span>
      {children}
      {error ? <span className="text-sm font-semibold text-red-700">{error}</span> : null}
    </label>
  );
}
